import React, { useMemo, useState } from 'react';
import { LogoLoader } from '@/components/ui/logo-loader';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { IconButtonCancel, IconButtonSave } from '@/components/ui/icon-buttons';
import { RightSidebar, SidebarButtonGroup, SidebarIconButton } from '@/components/ui/right-sidebar';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDateFormat } from '@/utils/date-formatter';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useDocumentActions } from '../hooks/useDocumentActions';
import { useProjectDocuments } from '../hooks/useProjectDocuments';
import { DocumentShareDialog } from './DocumentShareDialog';
import { apiRequest } from '@/lib/queryClient';

// Import sidebar icons
import iconShareSvg from '@/assets/web_share.svg';
import iconPrintSvg from '@/assets/web_print.svg';
import iconDeleteSvg from '@/assets/circle_delete.svg';

interface ProjectDocumentsTabProps {
  documents: any[];
  projectId: string;
  organizationId?: string;
  isLoading?: boolean;
  searchTerm: string;
  canManageDocuments?: boolean;
  onUploadClick?: (uploadHandler: () => void) => void;
}

export const ProjectDocumentsTab: React.FC<ProjectDocumentsTabProps> = ({
  documents,
  projectId,
  organizationId,
  isLoading = false,
  searchTerm,
  canManageDocuments = true,
  onUploadClick
}) => {
  const { t } = useTranslation(['pages.projectDetails']);
  const { formatDate } = useDateFormat();
  const [updatingDocumentId, setUpdatingDocumentId] = useState<string | null>(null);
  const [printLoadingId, setPrintLoadingId] = useState<string | null>(null);
  const [deletingDocumentId, setDeletingDocumentId] = useState<string | null>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set());
  const [isBulkActionLoading, setIsBulkActionLoading] = useState<string | null>(null);
  const {
    showDocumentUpload,
    uploadFile,
    isUploadingDocument,
    handleViewDocument,
    handleDeleteDocument,
    handleShareDocument,
    shareDialogOpen,
    setShareDialogOpen,
    shareUrl,
    shareExpiresIn,
    shareDocumentName,
    handleUploadClick,
    handleFileSelect,
    handleDocumentUpload,
    handleUploadCancel,
    deleteDocumentMutation
  } = useDocumentActions(projectId);

  const { updateDocumentGewerk } = useProjectDocuments({ projectId, organizationId });

  // Fetch gewerke list
  const { data: gewerkeResponse } = useQuery({
    queryKey: ['/api/gewerke', organizationId],
    queryFn: async () => {
      const url = organizationId 
        ? `/api/gewerke?organizationId=${encodeURIComponent(organizationId)}` 
        : '/api/gewerke';
      const response = await apiRequest(url);
      return await response.json();
    },
    enabled: !!organizationId,
  });

  const gewerke = gewerkeResponse?.data || [];

  // Expose handleUploadClick to parent via callback
  React.useEffect(() => {
    if (onUploadClick) {
      onUploadClick(canManageDocuments ? handleUploadClick : () => {});
    }
  }, [canManageDocuments, handleUploadClick, onUploadClick]);

  const handleDeleteDocumentWithLoading = async (documentId: string) => {
    if (!canManageDocuments) return;
    setDeletingDocumentId(documentId);
    try {
      await handleDeleteDocument(documentId);
    } finally {
      // Reset after a short delay to allow mutation to complete
      setTimeout(() => setDeletingDocumentId(null), 100);
    }
  };

  // Reset deleting state when mutation completes
  React.useEffect(() => {
    if (!deleteDocumentMutation.isPending && deletingDocumentId) {
      setDeletingDocumentId(null);
    }
  }, [deleteDocumentMutation.isPending, deletingDocumentId]);

  const handleGewerkChange = (documentId: string, gewerkId: string) => {
    if (!canManageDocuments) return;
    setUpdatingDocumentId(documentId);
    const onComplete = () => {
      setUpdatingDocumentId(null);
    };
    
    if (gewerkId === '_clear' || gewerkId === '') {
      updateDocumentGewerk(documentId, null, onComplete);
    } else {
      const gewerk = gewerke.find((g: any) => g.id === gewerkId);
      const gewerkName = gewerk ? gewerk.name : null;
      updateDocumentGewerk(documentId, gewerkName, onComplete);
    }
  };

  const filteredDocuments = useMemo(() => {
    if (!searchTerm) return documents;
    const searchLower = searchTerm.toLowerCase();
    return documents.filter((document: any) => {
      return (
        document.name?.toLowerCase().includes(searchLower) ||
        document.fileName?.toLowerCase().includes(searchLower) ||
        document.gewerk?.toLowerCase().includes(searchLower)
      );
    });
  }, [documents, searchTerm]);

  // Selection handlers
  const handleSelectDocument = (documentId: string, checked: boolean) => {
    setSelectedDocuments(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(documentId);
      } else {
        newSet.delete(documentId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedDocuments(new Set(filteredDocuments.map((doc: any) => doc.id)));
    } else {
      setSelectedDocuments(new Set());
    }
  };

  const isAllSelected = filteredDocuments.length > 0 && selectedDocuments.size === filteredDocuments.length;
  const hasSelection = selectedDocuments.size > 0;

  // Bulk action handlers
  const handleBulkShare = async () => {
    if (selectedDocuments.size === 0) return;
    // For now, share the first selected document (can be extended to share multiple)
    const firstDocId = Array.from(selectedDocuments)[0];
    const doc = documents.find((d: any) => d.id === firstDocId);
    if (doc) {
      handleShareDocument(doc);
    }
  };

  const handleBulkPrint = async () => {
    if (selectedDocuments.size === 0) return;
    setIsBulkActionLoading('print');
    try {
      // Print each selected document
      for (const docId of selectedDocuments) {
        const doc = documents.find((d: any) => d.id === docId);
        if (doc) {
          await handlePrintDocument(doc);
        }
      }
    } finally {
      setIsBulkActionLoading(null);
    }
  };

  const handleBulkDelete = async () => {
    if (!canManageDocuments) return;
    if (selectedDocuments.size === 0) return;
    setIsBulkActionLoading('delete');
    try {
      // Delete each selected document
      for (const docId of selectedDocuments) {
        await handleDeleteDocument(docId);
      }
      setSelectedDocuments(new Set());
    } finally {
      setIsBulkActionLoading(null);
    }
  };

  const inferMimeTypeFromName = (name: string): string | null => {
    const extension = name?.split('.').pop()?.toLowerCase();
    if (!extension) {
      return null;
    }

    switch (extension) {
      case 'pdf':
        return 'application/pdf';
      case 'png':
        return 'image/png';
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'gif':
        return 'image/gif';
      case 'webp':
        return 'image/webp';
      default:
        return null;
    }
  };

  const handlePrintDocument = async (doc: any) => {
    setPrintLoadingId(doc.id);
    let temporaryObjectUrl: string | null = null;
    try {
      const response = await apiRequest(`/api/documents/${doc.id}/file`);
      if (!response.ok) {
        throw new Error('Failed to fetch document');
      }

      const responseContentType = response.headers.get('content-type')?.toLowerCase() ?? '';
      let sourceUrl: string | null = null;
      let fileName = doc.fileName || doc.name || 'Document';
      let fileType = (doc.fileType as string | undefined)?.toLowerCase() || inferMimeTypeFromName(fileName) || '';

      if (responseContentType.includes('application/json')) {
        const data = await response.json();
        if (!data?.fileUrl) {
          throw new Error('No file URL returned by API');
        }

        sourceUrl = data.fileUrl;
        if (typeof data.fileName === 'string' && data.fileName.trim().length > 0) {
          fileName = data.fileName;
        }
        if (!fileType) {
          fileType = inferMimeTypeFromName(fileName) || '';
        }
      } else {
        const blob = await response.blob();
        temporaryObjectUrl = URL.createObjectURL(blob);
        sourceUrl = temporaryObjectUrl;
        if (!fileType) {
          fileType = blob.type?.toLowerCase() || inferMimeTypeFromName(fileName) || '';
        }
      }

      if (!sourceUrl) {
        throw new Error('Failed to resolve document URL for printing');
      }

      const isPdf = fileType === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');
      const isImage = fileType.startsWith('image/');

      // Open a new window for printing
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        throw new Error('Failed to open print window. Please allow popups for this site.');
      }

      if (isImage) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Print ${fileName}</title>
              <style>
                @media print {
                  body { margin: 0; padding: 0; }
                  img { max-width: 100%; height: auto; page-break-inside: avoid; }
                }
                @media screen {
                  body { margin: 0; padding: 20px; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
                  img { max-width: 100%; height: auto; }
                }
              </style>
            </head>
            <body>
              <img src="${sourceUrl}" alt="${fileName}" onload="setTimeout(() => { window.print(); }, 500)" />
            </body>
          </html>
        `);
        printWindow.document.close();
      } else if (isPdf) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Print ${fileName}</title>
              <style>
                body { margin: 0; padding: 0; overflow: hidden; }
                object { width: 100%; height: 100vh; border: none; }
              </style>
            </head>
            <body>
              <object data="${sourceUrl}" type="application/pdf" onload="setTimeout(() => { window.print(); }, 1500)">
                <p>PDF cannot be displayed. <a href="${sourceUrl}" target="_blank" rel="noopener noreferrer">Click here to download</a></p>
              </object>
            </body>
          </html>
        `);
        printWindow.document.close();

        // Also try to trigger print after a delay as fallback
        setTimeout(() => {
          try {
            printWindow.print();
          } catch (e) {
            console.error('Print error:', e);
          }
        }, 2000);
      } else {
        // For other file types, try to open directly
        printWindow.location.href = sourceUrl;

        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 1000);
        };
      }
    } catch (error) {
      console.error('Error printing document:', error);
    } finally {
      if (temporaryObjectUrl) {
        setTimeout(() => URL.revokeObjectURL(temporaryObjectUrl as string), 10000);
      }
      setPrintLoadingId(null);
    }
  };

  return (
    <div className="p-4 pt-2 -mt-[8px] h-full flex flex-col min-h-0">
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LogoLoader size="md" text={t('documents.loading')} showText />
        </div>
      ) : (
        <div className="flex gap-4 max-h-full min-h-0">
          {/* Main Content */}
          <div className="flex-1 bg-[#DDDDDD] rounded-[24px] p-[32px] flex flex-col min-h-0 max-h-full">
            {filteredDocuments.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-slate-400 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2">{t('documents.empty.title')}</h3>
                <p className="text-slate-600">{t('documents.empty.description')}</p>
              </div>
            ) : (
              <div className="bg-white border border-slate-200 overflow-hidden rounded-none shadow-none flex flex-col min-h-0 max-h-full">
                <div className="overflow-auto min-h-0">
                  <table className="w-full">
                    <thead className="sticky top-0 z-10 bg-white">
                      <tr className="table-header-row">
                        <th className="table-header-cell w-10">
                          <input
                            type="checkbox"
                            checked={isAllSelected}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </th>
                        <th className="table-header-cell">{t('documents.documentName')}</th>
                        <th className="table-header-cell">{t('documents.gewerk')}</th>
                        <th className="table-header-cell">{t('documents.fileSize')}</th>
                        <th className="table-header-cell">{t('documents.date')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDocuments.map((document: any) => (
                        <tr 
                          key={document.id} 
                          className={`table-body-row cursor-pointer ${selectedDocuments.has(document.id) ? 'bg-blue-50' : ''}`}
                          onClick={() => handleViewDocument(document)}
                        >
                          <td className="table-body-cell" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedDocuments.has(document.id)}
                              onChange={(e) => handleSelectDocument(document.id, e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                          <td className="table-body-cell">
                            <div className="flex items-center gap-2">
                              <div className="text-sm font-medium text-slate-900">{document.name}</div>
                              {document.organizationId && !document.projectId && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                                  {t('documents.fromOrganization', { ns: 'pages.projectDetails' }) || 'From Organization'}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="table-body-cell" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-2">
                              <Select
                                value={gewerke.find((g: any) => g.name === document.gewerk)?.id || ''}
                                onValueChange={(value) => handleGewerkChange(document.id, value)}
                                disabled={!canManageDocuments || updatingDocumentId === document.id}
                              >
                                <SelectTrigger className="textbox-select h-8 text-xs">
                                  <SelectValue placeholder={t('documents.selectGewerk')} />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="_clear" className="text-xs text-gray-400 italic">
                                    {t('components.ticket:fields.clearSelection')}
                                  </SelectItem>
                                  {gewerke.map((gewerk: any) => (
                                    <SelectItem key={gewerk.id} value={gewerk.id} className="text-xs">
                                      {gewerk.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {updatingDocumentId === document.id && (
                                <LogoLoader size="sm" />
                              )}
                            </div>
                          </td>
                          <td className="table-body-cell">
                            {(document.fileSize / 1024).toFixed(1)} KB
                          </td>
                          <td className="table-body-cell">
                            {formatDate(document.createdAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Right Sidebar Menu */}
          <RightSidebar>
            {/* Share Button */}
            <SidebarButtonGroup label={t('documents.share')} hideLabel>
              <SidebarIconButton
                icon={iconShareSvg}
                alt="Share"
                onClick={handleBulkShare}
                disabled={!hasSelection || isBulkActionLoading === 'share'}
                title={t('documents.share')}
              />
            </SidebarButtonGroup>

            {/* Print Button */}
            <SidebarButtonGroup label={t('documents.print')} hideLabel>
              <SidebarIconButton
                icon={iconPrintSvg}
                alt="Print"
                onClick={handleBulkPrint}
                disabled={!hasSelection}
                isLoading={isBulkActionLoading === 'print'}
                title={t('documents.print')}
              />
            </SidebarButtonGroup>

            {/* Delete Button */}
            <SidebarButtonGroup label={t('documents.delete')} hideLabel>
              {canManageDocuments && (
                <SidebarIconButton
                  icon={iconDeleteSvg}
                  alt="Delete"
                  onClick={handleBulkDelete}
                  disabled={!hasSelection}
                  isLoading={isBulkActionLoading === 'delete'}
                  title={t('documents.delete')}
                />
              )}
            </SidebarButtonGroup>
          </RightSidebar>
        </div>
      )}

      {/* Document Upload Modal */}
      <Dialog open={showDocumentUpload} onOpenChange={(open) => !open && handleUploadCancel()}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-hidden flex flex-col p-0 bg-[#E8E8E8] rounded-2xl" hideClose>
          {/* Custom Header with Cancel Button */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-300 bg-[#E8E8E8] rounded-t-2xl">
            <h2 
              className="text-lg font-semibold text-black"
              style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}
            >
              {t('documents.uploadModal.title')}
            </h2>
            <div className="flex items-center gap-2">
              <IconButtonCancel
                onClick={handleUploadCancel}
                disabled={isUploadingDocument}
                size={30}
                title={t('buttons.cancel', { ns: 'common' })}
              />
              <IconButtonSave
                onClick={handleDocumentUpload}
                disabled={isUploadingDocument || !uploadFile}
                size={30}
                title={t('buttons.save', { ns: 'common' })}
              />
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Upload Section */}
            <fieldset className="border border-gray-300 rounded-lg p-4 bg-white">
              <legend 
                className="px-2 text-sm font-medium text-black"
                style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}
              >
                {t('documents.uploadModal.selectFile')}
              </legend>
              <div className="flex flex-col items-center gap-3">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="document-file-input"
                />
                <Button 
                  variant="outline" 
                  onClick={() => document.getElementById('document-file-input')?.click()}
                  disabled={isUploadingDocument}
                  className="min-w-[150px]"
                >
                  {isUploadingDocument ? <LogoLoader size="sm" /> : t('documents.uploadModal.browseFiles', 'Browse Files...')}
                </Button>
                {uploadFile && (
                  <div className="w-full p-2 bg-gray-50 rounded text-sm text-center">
                    <strong>{t('documents.uploadModal.selected')}:</strong> {uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)
                  </div>
                )}
              </div>
            </fieldset>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <DocumentShareDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        shareUrl={shareUrl}
        expiresIn={shareExpiresIn}
        documentName={shareDocumentName}
      />
    </div>
  );
};
